import type { AccessTokenQuery, IsAuthenticatedQuery } from "../graphql/gmbh/schema/gmbh-types";
import { AccessTokenDocument, IsAuthenticatedDocument } from "../graphql/gmbh/schema/gmbh-types";
import { requestGmbh } from "../providers/utils/graphqlHelper";
import { sessionService } from "./sessionService";

class AuthService {
  private static instance: AuthService;
  private authState: {
    token: string | null;
    expiredAt: Date | null;
  } = {
    token: null,
    expiredAt: null,
  };

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  public async checkAuthentication(): Promise<boolean> {
    if (sessionService.isExpired()) {
      this.clearAuth();
      return false;
    }
    if (this.isAuthenticated()) {
      return true;
    }

    const response = await requestGmbh<IsAuthenticatedQuery>(IsAuthenticatedDocument);

    if (response.data.isAuthenticated) {
      await this.fetchAccessToken();
      return this.isAuthenticated();
    }

    return false;
  }

  public async fetchAccessToken(): Promise<void> {
    try {
      const response = await requestGmbh<AccessTokenQuery>(AccessTokenDocument);

      const accessToken = response.data.accessToken;
      if (accessToken?.token && accessToken?.expiresAt) {
        this.authState = {
          token: accessToken.token,
          expiredAt: new Date(accessToken.expiresAt),
        };
      }
    } catch (error) {
      console.error("Failed to fetch access token:", error);
      this.clearAuth();
    }
  }

  public clearAuth(): void {
    this.authState = {
      token: null,
      expiredAt: null,
    };
  }

  public getToken(): string | null {
    return this.authState.token;
  }

  public getExpiredAt(): Date | null {
    return this.authState.expiredAt;
  }

  public isAuthenticated(): boolean {
    if (sessionService.isExpired()) {
      this.clearAuth();
      return false;
    }
    if (!this.authState.token || !this.authState.expiredAt) {
      return false;
    }
    return new Date() < this.authState.expiredAt;
  }
}

export const authService = AuthService.getInstance();
